import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { environment } from 'src/environments/environment';
import { AnalyticsService } from '../analytics/analytics.service';
import { AnalyticsEvent } from '../analytics/interfaces/analytics-event.interface';
import { ContextService } from '../context/context.service';

interface PageInfo {
    url: string;                        // Current page URL where script was detected
    referrer: string | null;            // Page that referred to current page
    title: string | null;               // Document title of current page
    userAgent: string;                  // Browser user agent string
}

interface SecurityContext {
    cspPresent: boolean;                // Whether Content Security Policy is present
    httpsUsed: boolean;                 // Whether page is served over HTTPS
    cookieCount: number;                // Number of cookies present
    localStorageUsed: boolean;          // Whether localStorage is being used
    sessionStorageUsed: boolean;        // Whether sessionStorage is being used
    indexedDBUsed: boolean;             // Whether IndexedDB is available
    webWorkersSupported: boolean;       // Whether Web Workers are supported
    serviceWorkerActive: boolean;       // Whether Service Worker is active
}

interface SecurityFlags {
    isDataUrl: boolean;                 // Whether URL is a data: URL (high security risk)
    isBlobUrl: boolean;                 // Whether URL is a blob: URL (malware technique)
    isExtension: boolean;               // Whether URL is from browser extension
    crossOrigin: boolean;               // Whether script is from different origin
}

interface RiskFactors {
    suspiciousContent: boolean;         // Whether script contains suspicious content patterns
    untrustedDomain: boolean;           // Whether script is from untrusted domain
    dynamicInjection: boolean;          // Whether script was dynamically injected
    crossOrigin: boolean;               // Whether script is from cross-origin source
    noIntegrity: boolean;               // Whether script lacks integrity attribute
    lateLoading: boolean;               // Whether script loaded late after page initialization
}

interface RiskAssessment {
    level: string;                      // Risk level: low, medium, high, critical
    threatCount: number;                // Number of threats detected
    threatTypes: string[];              // Types of threats identified
    confidence: string;                 // Confidence level in assessment
    automated: boolean;                 // Whether assessment was automated
    factors: RiskFactors;               // Detailed risk factors breakdown
}

interface SuspiciousPattern {
    pattern: string;                    // Regular expression pattern that matched
    patternIndex: number;               // Index of pattern in detection rules
    src: string;                        // Source URL or identifier where pattern was found
    timestamp: string;                  // Timestamp when pattern was detected
    contentPreview: string;             // Preview of content containing the pattern
}

interface BaseSuspiciousActivity {
    timestamp: string;                  // ISO timestamp when activity was detected
    riskLevel: string;                  // Risk level of the suspicious activity
}

interface ScriptPatternSuspiciousActivity extends BaseSuspiciousActivity {
    type: 'script-patterns';            // Activity type identifier
    scriptId: string;                   // Unique identifier of the script
    patterns: SuspiciousPattern[];      // Array of suspicious patterns found
}

interface CSPViolationSuspiciousActivity extends BaseSuspiciousActivity {
    type: 'csp-violation';              // Activity type identifier
    violatedDirective: string;          // CSP directive that was violated
    effectiveDirective: string;         // Effective directive that blocked the resource
    originalPolicy: string;             // Original CSP policy string
    blockedURI: string;                 // URI that was blocked by CSP
    sourceFile: string;                 // Source file where violation occurred
    lineNumber: number;                 // Line number where violation occurred
}

interface DocumentWriteSuspiciousActivity extends BaseSuspiciousActivity {
    type: 'document-write';             // Activity type identifier
    content: string;                    // Content that was written to document
    stackTrace: string | undefined;     // Stack trace when document.write was called
}

interface PlacementInfo {
    parentElement: string | null;       // Parent HTML element tag name
    position: number;                   // Position index among all scripts
    documentReady: string;              // Document ready state when script was found
    inHead: boolean;                    // Whether script is located in document head
    inBody: boolean;                    // Whether script is located in document body
    nextSibling: string | null;         // Next sibling element tag name
    previousSibling: string | null;     // Previous sibling element tag name
}

interface ScriptLog {
    // CORE PROPERTIES (SENT BY SCRIPT)
    src: string;                        // Script source URL or 'inline'
    type: string;                       // Detection method (existing, dynamic, etc.)
    async: boolean;                     // Whether script loads asynchronously
    defer: boolean;                     // Whether script execution is deferred
    module: boolean;                    // Whether script is ES6 module
    integrity: string | null;           // Subresource integrity hash
    crossorigin: string | null;         // CORS attribute value
    contentHash: string | null;         // Generated content hash for fingerprinting
    suspiciousPatterns: SuspiciousPattern[];  // Detected threat patterns in content
    riskLevel: string;                  // Risk assessment (low/medium/high/critical)
    nonce: string | null;               // CSP nonce attribute
    referrerPolicy: string | null;      // Referrer policy attribute
    detectionMethods: string[];         // All methods that detected this script
    loadTime: number | null;            // Script loading time in milliseconds
    timestamp: string;                  // Log entry creation timestamp
    isWhitelisted: boolean;             // Whether domain is in whitelist
    timing: {
        timeFromPageLoad: number;       // Time elapsed since page load (only property sent)
    };
    placement: PlacementInfo | null;    // Script placement context in DOM
    securityFlags: SecurityFlags;       // Security-critical URL flags (optimized networkInfo)
    riskAssessment: RiskAssessment;     // Comprehensive risk evaluation
}

interface ScriptSecuritySummary {
    currentUrl: string;                 // Current page URL for context
    pageInfo: PageInfo;                 // Single pageInfo at root level (67% reduction)
    securityContext: SecurityContext;   // Single securityContext at root level (8% reduction)
    logs: ScriptLog[];                  // Optimized logs matching actual sent data
    suspiciousActivity: SuspiciousActivity[];   // Array of security threats and violations
}

type SuspiciousActivity =
    | ScriptPatternSuspiciousActivity
    | CSPViolationSuspiciousActivity
    | DocumentWriteSuspiciousActivity;


@Injectable({
    providedIn: 'root',
})
export class ScriptSecurityService {
    private readonly analyticsService = inject(AnalyticsService);
    public readonly route = inject(ActivatedRoute);
    private readonly contextService = inject(ContextService);

    constructor() {
        this.initializeScriptSecurityListening();
    }

    private initializeScriptSecurityListening(): void {
        document.addEventListener('scriptSecuritySummary', (event: CustomEvent<ScriptSecuritySummary>) => {
            this.handleScriptSecuritySummary(event.detail);
        });
    }

    private handleScriptSecuritySummary(summary: ScriptSecuritySummary): void {
        const { context } = this.contextService;
        const entityId = context.value.context.hotel.id ?? context.value.context.channel.id;

        try {
            if (summary.logs.length === 0 && summary.suspiciousActivity.length === 0) {
                return;
            }

            const scriptAnalyticsEvent: AnalyticsEvent<ScriptSecuritySummary> = {
                collection: 'bmng.scripts',
                entityId,
                data: summary,
            };

            if (!environment.production) {
                // TODO @Milo (VPDC-49741 - scripts tampering) Remove toggle once we're ready to go live
                if (environment.production) {
                    return;
                }

                if (!environment.production ) {
                    console.log('scriptAnalyticsEvent', scriptAnalyticsEvent);
                    return;
                }

                this.analyticsService.track(scriptAnalyticsEvent, true)
                    .then(success => {
                        if (!success) {
                            console.warn('Failed to send script security summary');
                        }
                    })
                    .catch(error => {
                        console.error('Error sending script security data:', error);
                    });
            }

        } catch (error) {
            console.error('Error processing script security summary:', error);
        }
    }
}
